/**
 * «Поля» карточки хозяйства: активные · архив · кандидаты сверки (AK-746).
 *
 * Урезанная таблица против общего экрана `/fields` (там два десятка колонок):
 * имя, отделение, учётная га, контур, занятие, план. Общий экран не
 * дублируется — из карточки на него уходит ссылка с преднастроенным фильтром.
 *
 * Массовые действия — по канону `frontend-vite/CLAUDE.md`: одна мутация, одна
 * инвалидация в `onSettled`, один итог словами с именами полей.
 */
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { Archive, ArchiveRestore, ExternalLink, MoveRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Stack } from '@/components/ui/stack';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatRuDateNumeric } from '@/lib/dateFmt';
import { cn } from '@/lib/utils';
import { splitArchiveReason } from './archiveReason';
import { formatHa } from './OverviewTab';
import { batchLimitNotice } from '@/lib/bulk/batchLimits';
import type { FarmFieldRow, FarmFieldsMode, FarmSummary } from './types';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

/**
 * Расхождение учётной и контурной в процентах — для приглушённого бейджа.
 * null, когда расхождения нет: бейдж «0 %» на каждой строке — шум.
 */
export function areaDeltaPct(bookHa: number, contourHa: number): number | null {
  if (!bookHa) return null;
  const pct = Math.round(((contourHa - bookHa) / bookHa) * 100);
  return pct === 0 ? null : pct;
}

const MODE_LABELS: Record<FarmFieldsMode, string> = {
  active: 'Активные',
  archived: 'Архив',
  reconcile: 'Кандидаты сверки',
};

export function FieldsTab({
  farmId,
  summary,
  mode,
  onModeChange,
  issue,
  onClearIssue,
  items,
  total,
  totalAreaHa,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  batchMax,
  search,
  onSearchChange,
  brigade,
  onBrigadeChange,
  selected,
  onToggleRow,
  onToggleAll,
  canWrite,
  canMove,
  onArchive,
  onMove,
  onRestore,
  busy,
  onAddField,
}: {
  farmId: string;
  summary: FarmSummary;
  mode: FarmFieldsMode;
  onModeChange: (mode: FarmFieldsMode) => void;
  issue: string | null;
  onClearIssue: () => void;
  items: readonly FarmFieldRow[];
  total: number;
  totalAreaHa: number;
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Потолок одной пачки — общий для всех терминальных кнопок выделения. */
  batchMax: number;
  search: string;
  onSearchChange: (value: string) => void;
  brigade: string | null;
  onBrigadeChange: (value: string | null) => void;
  selected: ReadonlySet<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: (ids: string[], checked: boolean) => void;
  canWrite: boolean;
  /** «В отделение…» — `PERM.fieldMoveToBrigade` (бэк tenant-level до AK-930). */
  canMove: boolean;
  onArchive: () => void;
  onMove: () => void;
  onRestore: () => void;
  busy: boolean;
  /** Завести поле на карте с предвыбранным хозяйством. Нет права — нет кнопки. */
  onAddField?: () => void;
}) {
  const ids = useMemo(() => items.map((r) => r.id), [items]);
  const allChecked = ids.length > 0 && ids.every((id) => selected.has(id));
  const someChecked = !allChecked && ids.some((id) => selected.has(id));
  const selectedCount = items.filter((r) => selected.has(r.id)).length;
  const selectedHa = items
    .filter((r) => selected.has(r.id))
    .reduce((sum, r) => sum + (r.area_ha ?? 0), 0);
  const archived = mode === 'archived';
  const limitNotice = batchLimitNotice(selectedCount, ids.length, batchMax);

  return (
    <Stack gap={4}>
      <Tabs value={mode} onValueChange={(v) => onModeChange(v as FarmFieldsMode)}>
        <TabsList>
          <TabsTrigger value="active">
            {MODE_LABELS.active} ({summary.fields_active})
          </TabsTrigger>
          <TabsTrigger value="archived">
            {MODE_LABELS.archived} ({summary.fields_archived})
          </TabsTrigger>
          <TabsTrigger value="reconcile">{MODE_LABELS.reconcile}</TabsTrigger>
        </TabsList>
      </Tabs>

      {issue && mode !== 'reconcile' && (
        // Пришли по сигналу с «Обзора» — человек обязан видеть, что список
        // сужен, и уметь вернуть его целиком одним кликом.
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted p-3 text-sm">
          <span className="flex-1">
            Показаны только поля по сигналу «здоровья данных».
          </span>
          <Button variant="outline" size="sm" onClick={onClearIssue}>
            Показать все
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={search}
          placeholder="Поиск по имени поля"
          className="max-w-xs"
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={brigade === null ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onBrigadeChange(null)}
          >
            Все отделения
          </Button>
          {summary.brigades.map((b) => (
            <Button
              key={b.id ?? 'none'}
              variant={brigade === (b.id ?? 'none') ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => onBrigadeChange(b.id ?? 'none')}
            >
              {b.name}
            </Button>
          ))}
        </div>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" asChild>
          <Link to="/fields" search={{ farm_id: farmId }}>
            Все поля в общем списке
            <ExternalLink className="ml-2 h-4 w-4" />
          </Link>
        </Button>
        {onAddField && (
          // Заведение — редкое действие и живёт во вкладке, а не в шапке
          // раздела (макет v2, AK-899).
          <Button variant="outline" size="sm" onClick={onAddField}>
            <Plus className="mr-2 h-4 w-4" />
            Поле
          </Button>
        )}
      </div>

      {canWrite && selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
          <span className="font-semibold">
            Выбрано {selectedCount} · {formatHa(selectedHa)}
          </span>
          {limitNotice && (
            // Обрезали выбор — говорим об этом. Молча обработать 200 из 500 и
            // не объяснить почему — худший вид «сделано наполовину».
            <span className="text-muted-foreground" data-testid="batch-limit-notice">
              {limitNotice}
            </span>
          )}
          <div className="flex-1" />
          {archived ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={onRestore}>
              <ArchiveRestore className="mr-2 h-4 w-4" />
              Восстановить
            </Button>
          ) : (
            <>
              {canMove && (
                <Button size="sm" variant="outline" disabled={busy} onClick={onMove}>
                  <MoveRight className="mr-2 h-4 w-4" />
                  В отделение…
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={onArchive}
                data-testid="fields-bulk-archive"
              >
                <Archive className="mr-2 h-4 w-4" />
                Архивировать…
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => onToggleAll(ids, false)}>
            Снять выделение
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-xs uppercase text-muted-foreground">
                  {canWrite && (
                    <th className="w-10 p-3">
                      <Checkbox
                        checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                        disabled={ids.length === 0}
                        onCheckedChange={(v) => onToggleAll(ids, v === true)}
                        aria-label="Выбрать показанные поля"
                      />
                    </th>
                  )}
                  <th className="p-3 text-left font-semibold">Поле</th>
                  <th className="p-3 text-left font-semibold">Отделение</th>
                  <th className="p-3 text-right font-semibold">Учётная, га</th>
                  <th className="p-3 text-right font-semibold">Контур, га</th>
                  {archived ? (
                    <>
                      <th className="p-3 text-left font-semibold">Причина</th>
                      <th className="p-3 text-left font-semibold">Когда и кто</th>
                    </>
                  ) : (
                    <>
                      <th className="p-3 text-left font-semibold">Занятие</th>
                      <th className="p-3 text-left font-semibold">План</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <FieldRow
                    key={row.id}
                    row={row}
                    archived={archived}
                    canWrite={canWrite}
                    checked={selected.has(row.id)}
                    onToggle={() => onToggleRow(row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              {loading ? 'Загружаю…' : `Показано ${items.length} из ${total}`}
            </span>
            {hasMore && (
              <Button
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={onLoadMore}
                data-testid="fields-load-more"
              >
                {loadingMore ? 'Гружу…' : 'Показать ещё'}
              </Button>
            )}
            <div className="flex-1" />
            {/* Итог считает ВСЮ выборку под фильтром, а не показанную страницу
                (правило AK-750): подпись обязана говорить про хозяйство. */}
            <span>
              Итого по фильтру:{' '}
              <span className="font-semibold text-foreground">
                {total} полей · {formatHa(totalAreaHa)} учётной
              </span>
            </span>
          </div>
        </CardContent>
      </Card>

      {!loading && items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {mode === 'archived'
            ? 'В архиве пусто — из этого хозяйства ничего не убирали.'
            : mode === 'reconcile'
              ? 'Кандидатов сверки нет: все поля хозяйства пришли из «Истории поля».'
              : 'Под фильтр ничего не попало.'}
        </p>
      )}
    </Stack>
  );
}

function FieldRow({
  row,
  archived,
  canWrite,
  checked,
  onToggle,
}: {
  row: FarmFieldRow;
  archived: boolean;
  canWrite: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const delta = areaDeltaPct(row.area_ha, row.contour_area_ha);
  const reason = splitArchiveReason(row.archive_reason);
  return (
    <tr className={cn('border-b border-border last:border-b-0', checked && 'bg-primary/5')}>
      {canWrite && (
        <td className="p-3">
          <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Поле ${row.name}`} />
        </td>
      )}
      <td className="p-3">
        <Link
          to="/fields/$fieldId"
          params={{ fieldId: row.id }}
          className="font-medium text-primary underline"
        >
          {row.name}
        </Link>
        {!row.is_arable && !archived && (
          <Badge variant="outline" className="ml-2">
            {row.land_category_name ?? 'угодье не указано'}
          </Badge>
        )}
      </td>
      <td className="p-3">
        {row.brigade_name ?? (
          <span className="text-muted-foreground">Без отделения</span>
        )}
      </td>
      <td className="p-3 text-right tabular-nums">{HA.format(row.area_ha)}</td>
      <td className="p-3 text-right tabular-nums text-muted-foreground">
        {HA.format(row.contour_area_ha)}
        {delta !== null && (
          <Badge variant="outline" className="ml-2">
            {delta > 0 ? '+' : '−'}
            {Math.abs(delta)}%
          </Badge>
        )}
      </td>
      {archived ? (
        <>
          <td className="p-3">
            {reason.label ? (
              <span>{reason.label}</span>
            ) : (
              <span className="text-muted-foreground">не указана</span>
            )}
            {reason.comment && (
              <span className="block text-xs text-muted-foreground">{reason.comment}</span>
            )}
          </td>
          <td className="p-3 text-muted-foreground">
            {formatRuDateNumeric(row.archived_at)}
            {row.archived_by_name ? ` · ${row.archived_by_name}` : ''}
          </td>
        </>
      ) : (
        <>
          <td className="p-3">
            {row.current_crop ?? <span className="text-muted-foreground">—</span>}
          </td>
          <td className="p-3">
            {row.plan_crop ?? <span className="text-muted-foreground">нет плана</span>}
          </td>
        </>
      )}
    </tr>
  );
}
